import { useEffect, useState } from 'react'
import { Outlet, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth.store'
import { authApi } from '@/lib/api'
import { Sidebar } from './Sidebar'
import { ToastContainer, ErrorHistoryFab } from '@/components/ui'
import { useKmRenderResume } from '@/hooks/useKmRenderResume'
import type { UserRole } from '@/types'

interface MainLayoutProps {
  requiredRole?: UserRole
}

export function MainLayout({ requiredRole }: MainLayoutProps) {
  const { isAuthenticated, role, clearAuth } = useAuthStore()
  const [sessionChecked, setSessionChecked] = useState(false)
  const [sessionValid, setSessionValid] = useState(true)
  useKmRenderResume()

  // Validate Flask session on mount — detect stale frontend auth
  useEffect(() => {
    if (!isAuthenticated) {
      setSessionChecked(true)
      return
    }
    authApi.check()
      .then(() => {
        setSessionValid(true)
        setSessionChecked(true)
      })
      .catch((err) => {
        const status = err?.response?.status
        if (status === 401 || status === 403) {
          // Flask session expired/missing — clear frontend auth
          clearAuth()
          setSessionValid(false)
        } else {
          // Network error / server not running — keep auth, show page anyway
          setSessionValid(true)
        }
        setSessionChecked(true)
      })
  }, [isAuthenticated, clearAuth])

  if (!sessionChecked) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-400">Проверка сессии...</div>
      </div>
    )
  }

  if (!isAuthenticated || !sessionValid) return <Navigate to="/login" replace />
  if (requiredRole && role !== requiredRole) {
    return <Navigate to={role === 'admin' ? '/admin' : '/worker'} replace />
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-y-auto">
        <div className="p-6">
          <Outlet />
        </div>
      </main>
      <ToastContainer />
      <ErrorHistoryFab />
    </div>
  )
}
